import { useQuery } from '@apollo/client/react';
import { MoveDiagonalIcon, PlusIcon } from 'lucide-react';
import { Link, useParams } from 'react-router';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';

import {
  formatDate,
  formatScore,
  GET_RISK_SCORECARD,
  type GetRiskScorecardResponse,
  getScoreColor
} from './graphql';

import { Button } from '@/components/ui/button';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const InvestmentsDirectIdDealScore = () => {
  const { id } = useParams<{ id: string }>();

  const { data, error, loading } = useQuery<GetRiskScorecardResponse>(
    GET_RISK_SCORECARD,
    {
      skip: !id,
      variables: { id }
    }
  );

  const historyItems = data?.node?.riskScorecardHistory ?? [];

  if (loading) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-5">
        Error loading risk scorecard: {error.message}
      </div>
    );
  }

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-2.5 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">All Scores</h2>
            {historyItems.length !== 0 && (
              <Button asChild>
                <Link to="add-score">
                  <PlusIcon />
                  Add Score
                </Link>
              </Button>
            )}
          </div>
          <div className="bg-card rounded-md border p-5">
            {historyItems.length === 0 ? (
              <Empty className="p-0!">
                <EmptyContent>
                  <Button asChild>
                    <Link to="add-score">
                      <PlusIcon />
                      Add Score
                    </Link>
                  </Button>
                </EmptyContent>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-0" />
                    <TableHead>Name</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Scoring Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...historyItems].reverse().map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="w-0">
                        <Button
                          asChild
                          size="icon"
                          variant="ghost"
                        >
                          <Link state={{ name: `Score ${index + 1}` }} to={item.id}>
                            <MoveDiagonalIcon />
                          </Link>
                        </Button>
                      </TableCell>
                      <TableCell>Score {index + 1}</TableCell>
                      <TableCell
                        className={cn(
                          'font-bold',
                          getScoreColor(item.totalScore)
                        )}
                      >
                        {formatScore(item.totalScore)}
                      </TableCell>
                      <TableCell>{formatDate(item.date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdDealScore;
